import { ArrowDownLeft, ArrowLeftRight, ArrowUpRight, FileText, HandCoins, MoreHorizontal, Pencil, ShoppingCart, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { fmtDate, fmtMoney } from '../../shared/format'
import { fmtAmount } from '../fx'
import type { AccountPeriodSummary, FinAccount } from '../types'
import { ACCOUNT_ICON } from '../visuals'
import { useStatementPerms } from '../bankStatements'
import { AccountBankInfo } from './AccountBankInfo'
import { BankLogo } from './banks/BankLogo'
import { BankStatementsPanel } from './credit/BankStatementsPanel'

type Props = {
  account: FinAccount
  summary?: AccountPeriodSummary
  periodLabel: string
  canWrite: boolean
  selected: boolean
  onSelect: () => void
  onEntry: (direction: 'in' | 'out') => void
  onTransfer: () => void
  onEdit: () => void
  onDelete: () => void
}

/** Hesap kartı: güncel bakiye, seçilen dönemin açılış/giriş/çıkış/kapanışı ve hızlı işlemler. */
export function AccountCard({ account: a, summary, periodLabel, canWrite, selected, onSelect, onEntry, onTransfer, onEdit, onDelete }: Props) {
  const { t } = useTranslation()
  const Icon = ACCOUNT_ICON[a.type]
  const negative = a.balance.startsWith('-')
  const statementsAllowed = useStatementPerms('company').read && (a.type === 'bank' || a.type === 'credit_card')
  const [statements, setStatements] = useState(false)

  return (
    <article className={cn('flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-card transition-shadow', selected && 'ring-2 ring-primary/40', !a.active && 'opacity-70')}>
      <div className="flex items-start gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          {a.bankLogoUrl ? <BankLogo url={a.bankLogoUrl} name={a.bankName} className="size-7" /> : <Icon className="size-5" aria-hidden />}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-semibold">{a.name}</h3>
          <p className="truncate text-xs text-muted-foreground">
            {t(`finance.accountType.${a.type}`)}
            {a.bankName ? ` · ${a.bankName}` : ''}
          </p>
        </div>
        {!a.active ? <Badge tone="neutral">{t('finance.passive')}</Badge> : null}
        {canWrite ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="size-8 text-muted-foreground" aria-label={t('finance.rowActions', { name: a.name })}>
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={onEdit}>
                <Pencil />
                {t('common.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onDelete} className="text-destructive [&_svg]:text-destructive">
                <Trash2 />
                {t('common.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>

      <div>
        <p className="text-xs text-muted-foreground">{t(a.type === 'credit_card' && negative ? 'finance.accounts.cardDebt' : 'finance.accounts.balance')}</p>
        <p className={cn('text-2xl font-semibold tabular-nums', negative && 'text-destructive')}>{fmtAmount(a.balance, a.currency)}</p>
        {a.currency !== 'TRY' ? (
          <p className="text-muted-foreground text-xs tabular-nums">
            {a.balanceTry
              ? t('finance.fx.tryEquivalent', { amount: fmtMoney(a.balanceTry), rate: a.fxRate, date: fmtDate(a.fxRateDate) })
              : t('finance.fx.noRate')}
          </p>
        ) : null}
      </div>

      <AccountBankInfo account={a} />

      <div className="rounded-lg bg-muted/40 p-3">
        <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{periodLabel}</p>
        {summary ? (
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
            <dt className="text-muted-foreground">{t('finance.accounts.opening')}</dt>
            <dd className="text-right tabular-nums">{fmtAmount(summary.opening, a.currency)}</dd>
            <dt className="flex items-center gap-1 text-success">
              <ArrowDownLeft className="size-3.5" aria-hidden />
              {t('finance.entry.in')}
            </dt>
            <dd className="text-right font-medium tabular-nums text-success">+{fmtAmount(summary.inflow, a.currency)}</dd>
            <dt className="flex items-center gap-1 text-destructive">
              <ArrowUpRight className="size-3.5" aria-hidden />
              {t('finance.entry.out')}
            </dt>
            <dd className="text-right font-medium tabular-nums text-destructive">−{fmtAmount(summary.outflow, a.currency)}</dd>
            <dt className="border-t pt-1.5 font-medium">{t('finance.accounts.closing')}</dt>
            <dd className="border-t pt-1.5 text-right font-semibold tabular-nums">{fmtAmount(summary.closing, a.currency)}</dd>
          </dl>
        ) : (
          <Skeleton className="h-20" />
        )}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <button type="button" onClick={onSelect} aria-pressed={selected} className="w-fit text-xs font-medium text-primary hover:underline">
          {t('finance.accounts.showMovements', { count: summary?.count ?? 0 })}
        </button>
        {statementsAllowed ? (
          <button type="button" onClick={() => setStatements(true)} className="flex w-fit items-center gap-1 text-xs font-medium text-primary hover:underline">
            <FileText className="size-3.5" aria-hidden />
            {t('finance.bankStatements.title')}
          </button>
        ) : null}
      </div>
      {statements ? (
        <Modal
          open
          size="2xl"
          onOpenChange={(open) => !open && setStatements(false)}
          icon={<FileText />}
          title={t('finance.bankStatements.accountTitle', { name: a.name })}
          footer={
            <Button type="button" variant="cancel" onClick={() => setStatements(false)}>
              {t('common.close')}
            </Button>
          }
        >
          <BankStatementsPanel type="account" id={a.id} scope="company" />
        </Modal>
      ) : null}

      {canWrite && a.active && a.type === 'credit_card' ? (
        <div className="mt-auto grid grid-cols-2 gap-2 border-t pt-3">
          <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => onEntry('out')}>
            <ShoppingCart />
            {t('finance.cards.spendShort')}
          </Button>
          <Button size="sm" variant="outline" onClick={onTransfer}>
            <HandCoins />
            {t('finance.cards.payDebtShort')}
          </Button>
        </div>
      ) : canWrite && a.active ? (
        <div className="mt-auto grid grid-cols-3 gap-2 border-t pt-3">
          <Button size="sm" variant="outline" className="text-success hover:text-success" onClick={() => onEntry('in')}>
            <ArrowDownLeft />
            {t('finance.entry.inShort')}
          </Button>
          <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => onEntry('out')}>
            <ArrowUpRight />
            {t('finance.entry.outShort')}
          </Button>
          <Button size="sm" variant="outline" onClick={onTransfer}>
            <ArrowLeftRight />
            {t('finance.accounts.transferShort')}
          </Button>
        </div>
      ) : null}
    </article>
  )
}
